import { Component, OnInit, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { PairingService } from './services/pairing.service';
import { BoothSyncService } from './services/booth-sync.service';
import { BoothService } from './services/booth.service';
import { AdminModalComponent } from './components/admin-modal/admin-modal.component';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, AdminModalComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  pairing  = inject(PairingService);
  sync     = inject(BoothSyncService);
  booth    = inject(BoothService);
  router   = inject(Router);
  location = inject(Location);

  private w = effect(() => {
    if (this.sync.resetRequested() && this.router.url.startsWith('/stand')) {
      this.pairing.flagInvalidated();
      this.pairing.clearIpadUuid();
      this.sync.disconnect();
    }
  }, { allowSignalWrites: true });

  get showSessionInvalidatedOverlay(): boolean {
    return this.pairing.sessionInvalidated() && this.router.url.startsWith('/stand');
  }

  /** The whole footer (Back/Home/Session) only makes sense on /stand/*
   *  screens — no active guest/session context on /pair or /tv. */
  get showFooter(): boolean {
    return this.router.url.startsWith('/stand');
  }

  /** Back/Home buttons don't make sense while already on Home. */
  get showBackHome(): boolean {
    return this.router.url.startsWith('/stand') && !this.router.url.startsWith('/stand/home');
  }

  /** Generic "previous step" — pops browser history instead of each screen
   *  declaring its own target route. */
  goBack() {
    this.location.back();
  }

  /** Dismisses the overlay and sends the device to /pair (with no ?session=),
   *  which shows the "Device not synced — please scan the new QR" state
   *  instead of the old "Invalid sync code" error. */
  rescan() {
    this.pairing.clearInvalidatedFlag();
    this.router.navigate(['/pair']);
  }

  async ngOnInit() {
    await this.restorePairingIfNeeded();
  }

  /**
   * Restores this device's booth pairing_uuid on ANY fresh page load — not
   * just when landing on Home. This matters because the iOS app's deeplink
   * return can open a brand-new browser tab pointed directly at a deep
   * route like /stand/result?guest_id=..., completely skipping Home.
   *
   * Previously this restore logic lived only in HomeComponent.ngOnInit(),
   * so on that new tab BoothSyncService.pairingUuid stayed null —
   * result.component's sync.emit('result_win'/'result_loss'/'assess_result')
   * silently no-op'd (see BoothSyncService.emit()'s console.warn guard),
   * and the TV stayed stuck on "Results are being analyzed" forever,
   * since it was waiting for an event that never actually got sent.
   *
   * No-ops harmlessly on the TV build too — there's never a saved
   * xo_ipad_pairing_uuid in that device's localStorage.
   */
  private async restorePairingIfNeeded() {
    const uuid = this.pairing.getSavedIpadUuid();
    if (!uuid) return;

    try {
      const res = await this.pairing.status(uuid);
      if (res.data.status === 'expired') {
        this.pairing.clearIpadUuid();
        this.pairing.flagInvalidated();
        return;
      }
    } catch {
      // network hiccup on the status check — still try to connect below;
      // the instant 'pairing.reset' event will catch a real problem.
    }

    this.sync.connect(uuid);
    this.pairing.startHeartbeatLoop(uuid);
  }
}
